import { useEffect, useMemo, useRef, useState } from "react";
import { Filter } from "lucide-react";
import { computeCausalLayout } from "@/lib/causalLayout";

const DIAGRAM_LEVELS = [
  { key: "Influences organisationnelles", label: "Influences organisationnelles", style: "border-violet-500/50 bg-violet-50" },
  { key: "Facteurs de supervision", label: "Facteurs de supervision", style: "border-green-500/50 bg-green-50" },
  { key: "Conditions préalables", label: "Conditions préalables", style: "border-orange-500/50 bg-orange-50" },
  { key: "Actes dangereux", label: "Actes dangereux", style: "border-blue-500/50 bg-blue-50" },
];


const NORMAL_LINK_COLOR = "#000000";
const MAIN_CHAIN_COLOR = "#DC2626";

export default function MeetingDiagramView({ analysis, factors, links }) {
  const [lines, setLines] = useState([]);
  const [svgSize, setSvgSize] = useState({ width: 0, height: 0 });
  const [showOnlyMainChain, setShowOnlyMainChain] = useState(false);
  const containerRef = useRef(null);
  const nodeRefs = useRef({});
  const layout = useMemo(() => computeCausalLayout(factors, links), [factors, links]);

  useEffect(() => {
    const recompute = () => {
      if (!containerRef.current) return;
      const container = containerRef.current;
      const rect = container.getBoundingClientRect();
      const scrollLeft = container.scrollLeft || 0;
      const scrollTop = container.scrollTop || 0;
      let maxX = 0, maxY = 0;
      Object.values(nodeRefs.current).forEach((el) => {
        if (!el) return;
        const r = el.getBoundingClientRect();
        maxX = Math.max(maxX, r.right - rect.left + scrollLeft);
        maxY = Math.max(maxY, r.bottom - rect.top + scrollTop);
      });
      setSvgSize({ width: maxX, height: maxY });
      setLines(links.map((l) => {
        const sourceEl = nodeRefs.current[l.ID_SOURCE];
        const targetEl = nodeRefs.current[l.ID_CIBLE === "INCIDENT" ? "INCIDENT" : l.ID_CIBLE];
        if (!sourceEl || !targetEl) return null;
        const sRect = sourceEl.getBoundingClientRect();
        const tRect = targetEl.getBoundingClientRect();
        return {
          id: l.ID_LIEN, statut: l.STATUT_LIEN,
          x1: sRect.right - rect.left + scrollLeft, y1: sRect.top + sRect.height / 2 - rect.top + scrollTop,
          x2: tRect.left - rect.left + scrollLeft, y2: tRect.top + tRect.height / 2 - rect.top + scrollTop,
        };
      }).filter(Boolean));
    };
    const t = setTimeout(recompute, 150);
    window.addEventListener("resize", recompute);
    return () => { clearTimeout(t); window.removeEventListener("resize", recompute); };
  }, [links, factors]);

  const factorsByLevel = (level) => factors.filter((f) => f.NIVEAU === level);

  return (
    <div ref={containerRef} className="relative max-w-[1600px] mx-auto" data-testid="meeting-diagram-view">
      {layout.mainChainLinkIds.size > 0 && (
        <div className="mb-4 flex justify-end">
          <button
            data-testid="meeting-toggle-main-chain-only"
            onClick={() => setShowOnlyMainChain((v) => !v)}
            className={`flex items-center gap-2 text-xs font-mono uppercase rounded-sm border px-3 py-2 transition-colors ${showOnlyMainChain ? "bg-red-500 text-white border-red-500" : "border-red-500/40 text-red-600 hover:bg-red-500/10"}`}
          >
            <Filter className="h-3.5 w-3.5" /> {showOnlyMainChain ? "Afficher tout le diagramme" : "Ne montrer que la chaîne principale"}
          </button>
        </div>
      )}
      <svg className="absolute top-0 left-0 pointer-events-none" style={{ width: svgSize.width || "100%", height: svgSize.height || "100%" }}>
        <defs>
          <marker id="meeting-arrow" markerWidth="4.5" markerHeight="4.5" refX="3.5" refY="2.25" orient="auto">
            <path d="M0,0 L4.5,2.25 L0,4.5 Z" style={{ fill: NORMAL_LINK_COLOR }} />
          </marker>
          <marker id="meeting-arrow-main-chain" markerWidth="4.5" markerHeight="4.5" refX="3.5" refY="2.25" orient="auto">
            <path d="M0,0 L4.5,2.25 L0,4.5 Z" style={{ fill: MAIN_CHAIN_COLOR }} />
          </marker>
        </defs>
        {lines.filter((l) => !showOnlyMainChain || layout.mainChainLinkIds.has(l.id)).map((l) => {
          const isMainChain = layout.mainChainLinkIds.has(l.id);
          return (
            <path
              key={l.id}
              d={`M ${l.x1} ${l.y1} C ${(l.x1 + l.x2) / 2} ${l.y1}, ${(l.x1 + l.x2) / 2} ${l.y2}, ${l.x2} ${l.y2}`}
              fill="none" strokeWidth={isMainChain ? 2 : 1}
              stroke={isMainChain ? MAIN_CHAIN_COLOR : NORMAL_LINK_COLOR}
              style={{ opacity: !isMainChain && l.statut === "Rejeté" ? 0.3 : 1 }}
              markerEnd={isMainChain ? "url(#meeting-arrow-main-chain)" : "url(#meeting-arrow)"}
            />
          );
        })}
      </svg>

      <div className="grid gap-x-6 gap-y-3 relative z-10" style={{ gridTemplateColumns: "repeat(5, minmax(200px, 1fr))" }}>
        {DIAGRAM_LEVELS.map((lvl, colIdx) => (
          <p key={lvl.key} className="font-mono text-sm uppercase text-foreground font-semibold" style={{ gridColumn: colIdx + 1, gridRow: 1 }}>{lvl.label}</p>
        ))}
        <p className="font-mono text-sm uppercase text-foreground font-semibold" style={{ gridColumn: 5, gridRow: 1 }}>Incident / Événement</p>

        {DIAGRAM_LEVELS.map((lvl, colIdx) => {
          const items = factorsByLevel(lvl.key);
          const linked = items.filter((f) => layout.linkedIds.has(f.ID_FACTEUR));
          const unlinked = items.filter((f) => !layout.linkedIds.has(f.ID_FACTEUR));
          return (
            <div key={lvl.key} style={{ display: "contents" }}>
              {linked.map((f) => (
                <div key={f.ID} ref={(el) => (nodeRefs.current[f.ID_FACTEUR] = el)} style={{ gridColumn: colIdx + 1, gridRow: layout.rowAssigned[f.ID_FACTEUR] + 2 }} className={`bg-card border rounded-md p-3 ${lvl.style} ${showOnlyMainChain && !layout.mainChainNodeIds.has(f.ID_FACTEUR) ? "hidden" : ""}`}>
                  <p className="text-xs text-foreground mb-1">{f.FAMILLE}</p>
                  <p className="text-sm font-medium leading-snug text-foreground">{f.FACTEUR}</p>
                  {f.JUSTIFICATION && (
                    <p data-testid={`meeting-diagram-node-justification-${f.ID_FACTEUR}`} className="text-xs text-foreground/80 leading-snug mt-1.5 pt-1.5 border-t border-border/60">
                      {f.JUSTIFICATION}
                    </p>
                  )}
                </div>
              ))}
              {!showOnlyMainChain && unlinked.map((f, idx) => (
                <div key={f.ID} ref={(el) => (nodeRefs.current[f.ID_FACTEUR] = el)} style={{ gridColumn: colIdx + 1, gridRow: layout.maxRow + 3 + idx }} className={`bg-card border rounded-md p-3 opacity-80 ${lvl.style}`}>
                  <p className="text-xs text-foreground mb-1">{f.FAMILLE}</p>
                  <p className="text-sm font-medium leading-snug text-foreground">{f.FACTEUR}</p>
                  {f.JUSTIFICATION && (
                    <p data-testid={`meeting-diagram-node-justification-${f.ID_FACTEUR}`} className="text-xs text-foreground/80 leading-snug mt-1.5 pt-1.5 border-t border-border/60">
                      {f.JUSTIFICATION}
                    </p>
                  )}
                </div>
              ))}
            </div>
          );
        })}

        <div ref={(el) => (nodeRefs.current["INCIDENT"] = el)} style={{ gridColumn: 5, gridRow: 2 }} className="bg-card border border-red-500/60 bg-red-50 rounded-md p-3">
          <p className="text-sm font-medium text-foreground">{analysis?.TYPE_EVENEMENT}</p>
        </div>
      </div>
      {factors.length === 0 && <p className="text-lg text-muted-foreground text-center py-20">Aucun facteur pour l'instant.</p>}
    </div>
  );
}
